import { Fragment, useCallback, useMemo, useState } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import FiltersProvinceList from 'system/geographic/components/province/list';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { Province } from 'shell/models/province';
import { FiltersFormProvince, ProvinceClient } from './types';
import { useFormik } from 'formik';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesProvince } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonProvinceCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import { authsObj, validationSchemaProvinceFilter, headCells } from './constants';
import { Typography } from '@mui/material';

const ProvinceListPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<ProvinceClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<ProvinceClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormProvince>();
  const {
    data,
    get: getProvinceList,
    error,
  } = useHttpFetch<HttpResponseList<Province>>(`${__MUNICIPALITY_API_URL__}/geo/provincia`, {
    data: {
      payload: {
        list: [],
      },
    },
  });

  const { delete: deleteProvince } = useHttpFetch<HttpResponse<Province>>(
    `${__MUNICIPALITY_API_URL__}/geo/provincia`
  );

  const provinceList: Array<ProvinceClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((province) => ({
      code: province.key.codice,
      region: province.regione.nome,
      name: province.nome,
    }));
  }, [data]);

  const onSubmit = useCallback(
    (formValues: FiltersFormProvince) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterProvinceForm.setFieldValue('page', 0);
      getProvinceList(queryString);
    },
    [getProvinceList]
  );

  const filterProvinceForm = useFormik({
    initialValues: initialValuesProvince,
    validationSchema: validationSchemaProvinceFilter,
    onSubmit,
  });
  useEffect(() => {
    const queryString = transformDataset(filterTable.values || initialValuesProvince);
    getProvinceList(queryString);
    filterProvinceForm.setValues(filterTable.values || initialValuesProvince);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterProvinceForm.values);
  }, [filterProvinceForm.values]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload) {
      return 0;
    }
    return data.payload.list.length;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterProvinceForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getProvinceList(queryString).then(() => {
        filterProvinceForm.setFieldValue('orders', orders);
      });
    },
    [filterProvinceForm.setFieldValue, getProvinceList, filterProvinceForm.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<ProvinceClient> => {
      return new Promise((resolve) => {
        navigate(`/sistema/geografiche/italia/provincia/dettaglio/${cell.code}`);
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<ProvinceClient> => {
      return new Promise((resolve) => {
        navigate(`/sistema/geografiche/italia/provincia/modifica/${cell.code}`);
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<ProvinceClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<ProvinceClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (page) => {
      filterProvinceForm.setFieldValue('page', page || 0);
    },
    [filterProvinceForm.values, filterProvinceForm.setValues]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterProvinceForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterProvinceForm.setValues(newDataset);
    },
    [filterProvinceForm.setFieldValue, filterProvinceForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonProvinceCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi provincia"
        addBtnLabel="Aggiungi nuova provincia"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/geografiche/italia/provincia/nuovo');
        }}
        title="Gestione Province"
      />
      <div className="pmi-my-3">
        <FiltersProvinceList filterProvinceForm={filterProvinceForm} />
      </div>
      {data?.payload ? (
        provinceList?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterProvinceForm.values.orders}
            activeActions={auths}
            page={filterProvinceForm.values.page}
            rowsPerPage={filterProvinceForm.values.rowsPerPage}
            body={provinceList}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Province"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Provincia"
        message={`Confermare la cancellazione del provincia ${
          deleteAlertModal.element?.name || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteProvince(`/${deleteAlertModal.element?.code}`).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Provincia"
        message={`La cancellazione del provincia ${
          deleteSuccessModal.element?.name || ''
        } è avvenuta con successo`}
        onClose={() => {
          const queryString = transformDataset(filterProvinceForm.values);
          getProvinceList(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset(filterProvinceForm.values);
          getProvinceList(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

ProvinceListPage.displayName = 'Province List page component';

export default ProvinceListPage;
